<template>
	<div class="basic-settings lok-scrollbar">
		<section class="basic-settings__boxes">
			<!-- graficke detaily -->
			<!--
			<div class="basic-settings__row">
				<div class="basic-settings__row-left">
					{{ translate("ui.graphic_details") }}
				</div>
				<div class="basic-settings__row-right">
					<select v-model="selectedDetails">
						<option
							v-for="(detail, i) in graphicDetailsOptions"
							:key="i"
							:value="detail"
						>
							{{ translate(`ui.${detail}`) }}
						</option>
					</select>
				</div>
			</div>
			-->
			<!-- hudba -->
			<div class="basic-settings__row">
				<div class="basic-settings__row-left">
					<input
						type="checkbox"
						id="animations_on"
						v-model="animationsEnabled"
					/>
					<label for="animations_on">{{
						translate("ui.animations")
					}}</label>
				</div>
			</div>
			<!-- hudba -->
			<div class="basic-settings__row">
				<div class="basic-settings__row-left">
					<input
						type="checkbox"
						id="music_on"
						v-model="musicEnabled"
					/>
					<label for="music_on">{{ translate("ui.music") }}</label>
				</div>
				<div class="basic-settings__row-right">
					<input
						type="range"
						id="music_volume"
						name="music_volume"
						min="0"
						max="100"
						v-model="selectedMusicVolume"
					/>
				</div>
			</div>
			<!-- zvuky -->
			<div class="basic-settings__row">
				<div class="basic-settings__row-left">
					<input
						type="checkbox"
						id="sounds_on"
						v-model="soundsEnabled"
					/>
					<label for="sounds_on">{{ translate("ui.sounds") }}</label>
				</div>
				<div class="basic-settings__row-right">
					<input
						type="range"
						id="sounds_volume"
						name="sounds_volume"
						min="0"
						max="100"
						v-model="selectedSoundsVolume"
					/>
				</div>
			</div>
			<!-- jazyk -->
			<div class="basic-settings__row">
				<div class="basic-settings__row-left">
					{{ translate("ui.language") }}
				</div>
				<div class="basic-settings__row-right">
					<select v-model="selectedLanguage">
						<option
							v-for="(lang, i) in languages"
							:key="i"
							:value="lang.code"
						>
							{{ lang.name }}
						</option>
					</select>
				</div>
			</div>
			<div class="basic-settings__row">
				<div class="basic-settings__row-left">
					{{ translate("ui.country") }}
				</div>
				<div class="basic-settings__row-right">
					<select v-model="selectedCountry">
						<option
							v-for="(country, i) in countries"
							:key="i"
							:value="country.codes[0]"
						>
							{{ country.translation }}
						</option>
					</select>
				</div>
			</div>
		</section>
		<HorizontalDecorDivider />
		<section class="basic-settings__buttons">
			<AppTextButton
				:type="StatusTypes.DEFAULT"
				:text="translate('button.go_to_capital')"
				@click="goToCapital"
			/>
			<!-- <AppTextButton
			  TODO: schovane iba na softlaunch
				:type="StatusTypes.DEFAULT"
				:text="translate('button.map_screenshot')"
				@click="makeScreenshot"
			/> -->
		</section>
		<div class="basic-settings__version">
			{{ `${translate("ui.version")}: ${appVersion}` }}
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import { countryISO2ToISO3 } from "@/config/countries";
import SoundService from "@/game/SoundService";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useSocketService, { EmitStores } from "@/hooks/useSocketService";
import useSettingsStore from "@/store/useSettingsStore";
import useUserStore from "@/store/useUserStore";
import { type GraphicDetails, StatusTypes } from "@/types/ui";
import type { AvailableTranslation } from "@/utils/translate";
import { translate, Translator } from "@/utils/translate";
import { appVersion } from "@/utils/url";
import { storeToRefs } from "pinia";
import { computed, markRaw, ref, watch } from "vue";

const emitter = markRaw(useEmitter());
const socketService = markRaw(useSocketService());
const soundService = markRaw(SoundService.getInstance());

const settingsStore = useSettingsStore();
const {
	graphicDetails,
	musicOn,
	soundsOn,
	musicVolume,
	soundsVolume,
	animationsOn,
} = storeToRefs(settingsStore);

const userStore = useUserStore();
const { country, language } = storeToRefs(userStore);

const selectedDetails = ref<GraphicDetails>(graphicDetails.value);
const musicEnabled = ref<boolean>(musicOn.value);
const soundsEnabled = ref<boolean>(soundsOn.value);
const animationsEnabled = ref<boolean>(animationsOn.value);

const selectedMusicVolume = ref<number>(musicVolume.value);
const selectedSoundsVolume = ref<number>(soundsVolume.value);

const selectedLanguage = ref<string>(language.value);
const selectedCountry = ref<string>(country.value);

const emit = defineEmits(["languageChanged"]);

const languages = computed(() =>
	Translator.getInstance()
		.getAvailableTranslations()
		.map((e: AvailableTranslation): { code: string; name: string } => ({
			code: e.code,
			name: e.name,
		})),
);

const countries = computed(
	(): { translation: string; codes: [string, string] }[] => {
		const countries = [] as {
			translation: string;
			codes: [string, string];
		}[];
		for (const country of countryISO2ToISO3) {
			countries.push({
				translation: translate(`country.${country[1]}`),
				codes: country,
			});
		}
		countries.sort(
			(
				a: { translation: string; codes: [string, string] },
				b: { translation: string; codes: [string, string] },
			): number => {
				return a.translation.localeCompare(
					b.translation,
					language.value,
				);
			},
		);
		return countries;
	},
);

const emitGenericClick = (): void => {
	soundService.playGenerickClick();
};

// const graphicDetailsOptions = computed((): GraphicDetails[] => [
// 	GraphicDetails.LOW,
// 	GraphicDetails.MEDIUM,
// 	GraphicDetails.HIGH,
// ]);

watch(
	(): GraphicDetails => selectedDetails.value,
	(value: GraphicDetails): void => {
		emitGenericClick();
		settingsStore.setGraphicDetails(value);
	},
	{ immediate: true },
);

watch(
	(): boolean => animationsEnabled.value,
	(value: boolean): void => {
		emitGenericClick();
		settingsStore.setAnimations(value);
	},
	{ immediate: true },
);

watch(
	(): boolean => musicEnabled.value,
	(value: boolean): void => {
		emitGenericClick();
		settingsStore.setMusic(value);
	},
	{ immediate: true },
);

watch(
	(): boolean => soundsEnabled.value,
	(value: boolean): void => {
		emitGenericClick();
		settingsStore.setSounds(value);
	},
	{ immediate: true },
);

watch(
	(): number => selectedMusicVolume.value,
	(value: number): void => {
		settingsStore.setMusicVolume(+value);
		emitGenericClick();
	},
	{ immediate: true },
);

watch(
	(): number => selectedSoundsVolume.value,
	(value: number): void => {
		settingsStore.setSoundsVolume(+value);
		emitGenericClick();
		emitter.emit(EmitterEvents.SoundVolumeUpdated);
	},
	{ immediate: true },
);

watch(
	(): string => selectedCountry.value,
	(value: string): void => {
		emitGenericClick();
		userStore.setCountry(value.toString());
	},
	{ immediate: true },
);

watch(
	(): string => selectedLanguage.value,
	async (value: string, oldValue: string): Promise<void> => {
		if (value !== oldValue) {
			emitGenericClick();
			userStore.setLanguage(value.toString());

			// NOTE: since we refresh the component by :key in the next step, we save possible changes in settings, mainly language, without waiting for the next tick
			await socketService.emitSaveRequest({
				stores: [EmitStores.SETTINGS, EmitStores.USER],
			});

			emitter.emit(EmitterEvents.LanguageChanged);

			// NOTE: force redraw for buttons, tabs needed for ShrinkText
			emit("languageChanged");
		}
	},
	// WARNING: this should not be immediate, causes infinite loop
	{ immediate: false },
);

const goToCapital = (): void => {
	emitter.emit(EmitterEvents.PhaserGoToCapital);
	emitGenericClick();
};

// const makeScreenshot = (): void => {
// 	emitter.emit(EmitterEvents.PhaserMakeScreenshot);
// 	emitGenericClick();
// };
</script>

<style lang="scss" scoped>
.basic-settings {
	width: 100%;
	@include flex-col-start;
	position: relative;
	gap: 1rem;
	padding: 1.25rem;
	&__boxes {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		width: 100%;
	}
	&__buttons {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}
	&__version {
		color: #f2dcb5;
		font-size: 0.688rem;
	}

	&__row {
		display: flex;
		justify-content: space-between;
		width: 100%;
		font-size: 0.813rem;

		&-left {
			display: flex;
			align-items: center;
			color: #fff3dd;
			line-height: 1rem;
			input[type="checkbox"] {
				margin-right: 0.35rem;
			}
			label {
				cursor: pointer;
				margin-bottom: 0.15rem;
			}
		}
	}

	:deep(.horizontal-decor-divider) {
		width: 100%;
	}
}
</style>
